<template>
  <div class="sku-list">
    <el-card class="main-card">
      <div slot="header" class="card-header">
        <div class="header-left">
          <el-button type="text" icon="el-icon-back" @click="goBack" class="back-button">
            <span>返回SPU管理</span>
          </el-button>
          <div class="title-section">
            <h2 class="page-title">SKU管理</h2>
            <el-tag v-if="spuId" type="info" size="medium" class="spu-tag">
              <i class="el-icon-goods"></i>
              <span>SPU: {{ spuName }}</span>
            </el-tag>
          </div>
        </div>
        <el-button type="primary" size="small" @click="handleAdd" :disabled="!spuId" class="add-button">
          <i class="el-icon-plus"></i>新增SKU
        </el-button>
      </div>
      <el-table 
        :data="tableData" 
        border 
        stripe 
        v-loading="loading"
        class="sku-table"
        :row-class-name="getRowClassName">
        <el-table-column prop="id" label="ID" width="70" align="center"></el-table-column>
        <el-table-column prop="skuId" label="SKU ID" width="140" align="center"></el-table-column>
        <el-table-column label="商品信息" width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <div v-if="scope.row.product" class="product-info">
              <span class="product-name">{{ scope.row.product.productName }}</span>
            </div>
            <span v-else class="empty-cell">-</span>
          </template>
        </el-table-column>
        <el-table-column label="品牌信息" width="110" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.brand" size="small" type="success">{{ scope.row.brand.shortName || scope.row.brand.nameCn }}</el-tag>
            <span v-else class="empty-cell">-</span>
          </template>
        </el-table-column>
        <el-table-column label="店铺信息" width="200">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.shop" size="small" type="info">{{ scope.row.shop.shortName || scope.row.shop.nameCn }}</el-tag>
            <span v-else class="empty-cell">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="color" label="颜色" width="90" align="center"></el-table-column>
        <el-table-column prop="size" label="尺码" width="90" align="center"></el-table-column>
        <el-table-column prop="stock" label="库存" width="120" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.stock <= 10" type="danger" size="medium" class="stock-tag">{{ scope.row.stock }}</el-tag>
            <el-tag v-else-if="scope.row.stock <= 50" type="warning" size="medium" class="stock-tag">{{ scope.row.stock }}</el-tag>
            <el-tag v-else type="success" size="medium" class="stock-tag">{{ scope.row.stock }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleStockAdjust(scope.row)" class="action-btn-stock">库存调整</el-button>
            <el-button type="text" size="small" @click="handleStockRecord(scope.row)" class="action-btn-record">库存记录</el-button>
            <el-button type="text" size="small" @click="handleEdit(scope.row)" class="action-btn-edit">编辑</el-button>
            <el-button type="text" size="small" @click="handleDelete(scope.row)" class="action-btn-delete">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrapper">
        <el-pagination
          v-if="spuId"
          class="pagination"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="query.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="query.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
      <el-empty v-if="!spuId" description="请先从SPU管理页面选择SPU"></el-empty>
    </el-card>

    <el-dialog :title="skuDialogTitle" :visible.sync="skuDialogVisible" width="600px">
      <el-form :model="skuForm" :rules="skuRules" ref="skuForm" label-width="100px">
        <el-divider content-position="left">SPU 信息</el-divider>
        <el-form-item label="SPU ID">
          <el-input v-model="currentSpu.spuId" disabled></el-input>
        </el-form-item>
        <el-form-item label="商品名称">
          <el-input v-model="currentSpu.productName" disabled></el-input>
        </el-form-item>
        <el-form-item label="颜色">
          <el-input v-model="skuForm.color" disabled></el-input>
        </el-form-item>
        <el-form-item label="尺码">
          <el-input v-model="skuForm.size" disabled></el-input>
        </el-form-item>
        
        <el-divider content-position="left">SKU 信息</el-divider>
        <el-form-item label="选择店铺" prop="sid">
          <el-select v-model="skuForm.sid" placeholder="请选择店铺" style="width: 100%;" clearable filterable>
            <el-option
              v-for="shop in availableShops"
              :key="shop.sid"
              :label="shop.shortName || shop.nameCn"
              :value="shop.sid">
              <span>{{ shop.shortName || shop.nameCn }}</span>
              <span style="float:right;color:#8492a6;font-size:13px">{{ shop.address }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item v-if="!isEdit" label="初始库存" prop="stock">
          <el-input-number v-model="skuForm.stock" :min="0" style="width: 100%;"></el-input-number>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="skuDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog title="库存调整" :visible.sync="stockAdjustDialogVisible" width="500px">
      <el-form :model="stockAdjustForm" label-width="100px">
        <el-form-item label="SKU ID">
          <el-input v-model="stockAdjustForm.skuId" disabled></el-input>
        </el-form-item>
        <el-form-item label="当前库存">
          <el-input v-model="stockAdjustForm.currentStock" disabled></el-input>
        </el-form-item>
        <el-form-item label="调整方式">
          <el-radio-group v-model="stockAdjustForm.adjustType">
            <el-radio label="add">增加库存</el-radio>
            <el-radio label="reduce">减少库存</el-radio>
            <el-radio label="set">设置库存</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="数量" prop="amount">
          <el-input-number v-model="stockAdjustForm.amount" :min="1" style="width: 100%;"></el-input-number>
        </el-form-item>
        <el-form-item label="调整后库存">
          <el-input :value="adjustedStock" disabled></el-input>
        </el-form-item>
        <el-form-item label="备注">
          <el-input type="textarea" v-model="stockAdjustForm.remark" placeholder="请输入备注"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="stockAdjustDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleStockAdjustSubmit" :loading="stockAdjustLoading">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog title="库存操作记录" :visible.sync="stockRecordDialogVisible" width="800px">
      <el-table :data="stockRecordList" border stripe v-loading="stockRecordLoading">
        <el-table-column prop="beforeStock" label="调整前库存" width="120" align="center"></el-table-column>
        <el-table-column label="变化" width="100" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.changeAmount > 0" style="color: #67c23a">+{{ scope.row.changeAmount }}</span>
            <span v-else style="color: #f56c6c">{{ scope.row.changeAmount }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="afterStock" label="调整后库存" width="120" align="center"></el-table-column>
        <el-table-column prop="operator" label="操作人" width="100" align="center"></el-table-column>
        <el-table-column prop="operateTime" label="操作时间" width="180" align="center"></el-table-column>
        <el-table-column prop="remark" label="备注" show-overflow-tooltip></el-table-column>
      </el-table>
      <div class="pagination-wrapper">
        <el-pagination
          class="pagination"
          @size-change="handleRecordSizeChange"
          @current-change="handleRecordCurrentChange"
          :current-page="recordQuery.pageNum"
          :page-sizes="[10, 20, 50]"
          :page-size="recordQuery.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="stockRecordTotal">
        </el-pagination>
      </div>
      <span slot="footer">
        <el-button @click="stockRecordDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'SkuManage',
  data() {
    return {
      tableData: [],
      total: 0,
      loading: false,
      query: {
        pageNum: 1,
        pageSize: 10
      },
      spuId: null,
      spuName: '',
      skuDialogVisible: false,
      skuDialogTitle: '',
      skuForm: {
        id: null,
        skuId: '',
        pid: '',
        spuId: '',
        sid: '',
        color: '',
        size: '',
        stock: 0
      },
      isEdit: false,
      allProducts: [],
      allShops: [],
      availableShops: [],
      currentSpu: {
        spuId: '',
        productName: '',
        color: '',
        size: ''
      },
      skuRules: {
        sid: [{ required: true, message: '请选择店铺', trigger: 'change' }],
        stock: [{ required: true, message: '请输入初始库存', trigger: 'blur' }]
      },
      submitLoading: false,
      stockAdjustDialogVisible: false,
      stockAdjustForm: {
        skuId: '',
        currentStock: 0,
        adjustType: 'add',
        amount: 1,
        remark: ''
      },
      stockAdjustLoading: false,
      stockRecordDialogVisible: false,
      stockRecordList: [],
      stockRecordLoading: false,
      stockRecordTotal: 0,
      recordQuery: {
        pageNum: 1,
        pageSize: 10
      },
      currentRecordSkuId: ''
    }
  },
  computed: {
    adjustedStock() {
      if (this.stockAdjustForm.adjustType === 'add') {
        return this.stockAdjustForm.currentStock + this.stockAdjustForm.amount;
      } else if (this.stockAdjustForm.adjustType === 'reduce') {
        return this.stockAdjustForm.currentStock - this.stockAdjustForm.amount;
      } else {
        return this.stockAdjustForm.amount;
      }
    }
  },
  async mounted() {
    if (this.$route.query.spuId) {
      this.spuId = this.$route.query.spuId;
      this.spuName = this.$route.query.spuName || '';
      await this.loadData();
      await this.loadProducts();
      await this.loadShops();
    }
  },
  watch: {
    '$route.query.spuId'(newVal) {
      if (newVal) {
        this.spuId = newVal
        this.spuName = this.$route.query.spuName || ''
        this.query.pageNum = 1
        this.loadData()
      }
    }
  },
  methods: {
    getRowClassName({ rowIndex }) {
      return rowIndex % 2 === 0 ? 'even-row' : 'odd-row';
    },
    goBack() {
      this.$router.push({ name: 'Spu' })
    },
    async loadData() {
      if (!this.spuId) return
      
      this.loading = true
      try {
        const res = await this.$http.get(`/sku/page/${this.spuId}`, { params: this.query })
        let data = res
        if (res.data && res.data.code === 200) {
          data = res.data
        } else if (res.code === '200' || res.code === 200) {
          data = res
        }
        if (data.code === '200' || data.code === 200) {
          if (data.data && data.data.records) {
            this.tableData = data.data.records
            this.total = data.data.total
          } else if (data.data) {
            this.tableData = data.data.list || data.data || []
            this.total = data.data.total || 0
          } else {
            this.tableData = []
            this.total = 0
          }
        }
      } catch (error) {
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },
    async loadProducts() {
      try {
        // 先获取所有商品，不分页
        const res = await this.$http.get('/productNew/page', { params: { pageNum: 1, pageSize: 1000 } });
        if (res.code === '200' || res.code === 200) {
          this.allProducts = res.data.list || res.data.records || [];
        } else if (res.data && (res.data.code === '200' || res.data.code === 200)) {
          this.allProducts = res.data.data.list || res.data.data.records || [];
        }
      } catch (error) {
        console.error('加载商品列表失败', error);
      }
    },
    async loadShops() {
      try {
        const res = await this.$http.get('/shop/list');
        if (res.code === '200' || res.code === 200) {
          this.allShops = res.data || [];
        } else if (res.data && (res.data.code === '200' || res.data.code === 200)) {
          this.allShops = res.data.data || [];
        }
      } catch (error) {
        console.error('加载店铺列表失败', error);
      }
    },
    async handleAdd() {
      this.skuDialogTitle = '新增SKU';
      // 获取SPU信息
      const spu = await this.getSpuInfo(this.spuId);
      if (spu) {
        this.currentSpu = {
          spuId: spu.spuId,
          productName: spu.product ? spu.product.productName : '',
          color: spu.color,
          size: spu.size
        };
        // 加载可用店铺 - 这里可以根据品牌筛选，暂时显示所有店铺
        this.availableShops = this.allShops;
      }
      this.skuForm = {
        id: null,
        skuId: '',
        pid: spu ? spu.pid : '',
        spuId: this.spuId,
        sid: '',
        color: spu ? spu.color : '',
        size: spu ? spu.size : '',
        stock: 0
      };
      this.isEdit = false;
      this.skuDialogVisible = true;
    },
    async handleEdit(row) {
      this.skuDialogTitle = '编辑SKU';
      // 获取SPU信息
      const spu = await this.getSpuInfo(this.spuId);
      if (spu) {
        this.currentSpu = {
          spuId: spu.spuId,
          productName: spu.product ? spu.product.productName : '',
          color: spu.color,
          size: spu.size
        };
        this.availableShops = this.allShops;
      }
      this.skuForm = { 
        id: row.id, 
        skuId: row.skuId, 
        pid: row.pid, 
        spuId: row.spuId, 
        sid: row.sid,
        color: spu ? spu.color : row.color, 
        size: spu ? spu.size : row.size, 
        stock: row.stock 
      };
      this.isEdit = true;
      this.skuDialogVisible = true;
    },
    async getSpuInfo(spuId) {
      try {
        const res = await this.$http.get(`/spu/detail/${spuId}`);
        let data = res;
        if (res.data && res.data.code === 200) {
          data = res.data;
        } else if (res.code === '200' || res.code === 200) {
          data = res;
        }
        if (data.code === '200' || data.code === 200) {
          return data.data;
        }
      } catch (error) {
        console.error('获取SPU信息失败', error);
      }
      return null;
    },
    handleStockAdjust(row) {
      this.stockAdjustForm = {
        skuId: row.skuId,
        currentStock: row.stock,
        adjustType: 'add',
        amount: 1,
        remark: ''
      };
      this.stockAdjustDialogVisible = true;
    },
    async handleStockAdjustSubmit() {
      let changeAmount = 0;
      if (this.stockAdjustForm.adjustType === 'add') {
        changeAmount = this.stockAdjustForm.amount;
      } else if (this.stockAdjustForm.adjustType === 'reduce') {
        changeAmount = -this.stockAdjustForm.amount;
      } else {
        changeAmount = this.stockAdjustForm.amount - this.stockAdjustForm.currentStock;
      }

      this.stockAdjustLoading = true;
      try {
        const res = await this.$http.post('/sku/stock/adjust', {
          skuId: this.stockAdjustForm.skuId,
          changeAmount: changeAmount,
          remark: this.stockAdjustForm.remark
        });
        let success = false;
        if (res.code === '200' || res.code === 200) {
          success = true;
        } else if (res.data && (res.data.code === '200' || res.data.code === 200)) {
          success = true;
        }

        if (success) {
          this.$message.success('库存调整成功');
          this.stockAdjustDialogVisible = false;
          await this.loadData();
        } else {
          let message = '库存调整失败';
          if (res.message) {
            message = res.message;
          } else if (res.data && res.data.message) {
            message = res.data.message;
          }
          this.$message.error(message);
        }
      } catch (error) {
        this.$message.error('库存调整失败');
      } finally {
        this.stockAdjustLoading = false;
      }
    },
    handleStockRecord(row) {
      this.currentRecordSkuId = row.skuId;
      this.recordQuery.pageNum = 1;
      this.loadStockRecords();
      this.stockRecordDialogVisible = true;
    },
    async loadStockRecords() {
      this.stockRecordLoading = true;
      try {
        const res = await this.$http.get(`/sku/${this.currentRecordSkuId}/stock-records/page`, {
          params: {
            pageNum: this.recordQuery.pageNum,
            pageSize: this.recordQuery.pageSize
          }
        });
        let data = res;
        if (res.data && res.data.code === 200) {
          data = res.data;
        } else if (res.code === '200' || res.code === 200) {
          data = res;
        }
        if (data.code === '200' || data.code === 200) {
          if (data.data && data.data.records) {
            this.stockRecordList = data.data.records;
            this.stockRecordTotal = data.data.total;
          } else if (data.data) {
            this.stockRecordList = data.data.list || data.data || [];
            this.stockRecordTotal = data.data.total || 0;
          }
        }
      } catch (error) {
        this.$message.error('加载库存记录失败');
      } finally {
        this.stockRecordLoading = false;
      }
    },
    handleRecordSizeChange(val) {
      this.recordQuery.pageSize = val;
      this.loadStockRecords();
    },
    handleRecordCurrentChange(val) {
      this.recordQuery.pageNum = val;
      this.loadStockRecords();
    },
    handleEdit(row) {
      this.skuDialogTitle = '编辑SKU'
      this.skuForm = { id: row.id, skuId: row.skuId, pid: row.pid, spuId: row.spuId, color: row.color, size: row.size, stock: row.stock }
      this.isEdit = true
      this.skuDialogVisible = true
    },
    async handleStockChange(row, changeAmount) {
      const newStock = row.stock + changeAmount
      if (newStock < 0) {
        this.$message.warning('库存不能为负数')
        return
      }
      
      const confirmMessage = changeAmount > 0 
        ? `确定要增加 ${Math.abs(changeAmount)} 库存吗？`
        : `确定要减少 ${Math.abs(changeAmount)} 库存吗？`
        
      this.$confirm(confirmMessage, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          // 调用后端接口更新库存
          const res = await this.$http.post('/sku/stock/update', {
            skuId: row.skuId,
            stock: newStock
          })
          let success = false
          let message = '操作失败'
          if (res.code === '200' || res.code === 200) {
            success = true
          } else if (res.data && (res.data.code === '200' || res.data.code === 200)) {
            success = true
          }
          if (res.message) message = res.message
          if (res.data && res.data.message) message = res.data.message
          
          if (success) {
            this.$message.success('库存更新成功')
            await this.loadData()
          } else {
            this.$message.error(message)
          }
        } catch (error) {
          this.$message.error('库存更新失败')
        }
      }).catch(() => {})
    },
    async handleDelete(row) {
      this.$confirm('确定要删除该SKU吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$http.delete(`/sku/${row.id}`)
          let success = false
          let message = '删除失败'
          if (res.code === '200' || res.code === 200) {
            success = true
          } else if (res.data && (res.data.code === '200' || res.data.code === 200)) {
            success = true
          }
          if (res.message) message = res.message
          if (res.data && res.data.message) message = res.data.message
          
          if (success) {
            this.$message.success('删除成功')
            await this.loadData()
          } else {
            this.$message.error(message)
          }
        } catch (error) {
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    async handleSubmit() {
      this.$refs.skuForm.validate(async valid => {
        if (valid) {
          this.submitLoading = true
          try {
            const url = this.skuForm.id ? `/sku/${this.skuForm.id}` : '/sku'
            const method = this.skuForm.id ? 'put' : 'post'
            const res = await this.$http[method](url, this.skuForm)
            let success = false
            let message = '操作失败'
            if (res.code === '200' || res.code === 200) {
              success = true
            } else if (res.data && (res.data.code === '200' || res.data.code === 200)) {
              success = true
            }
            if (res.message) message = res.message
            if (res.data && res.data.message) message = res.data.message
            
            if (success) {
              this.$message.success('操作成功')
              this.skuDialogVisible = false
              await this.loadData()
            } else {
              this.$message.error(message)
            }
          } catch (error) {
            this.$message.error('操作失败')
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    handleSizeChange(val) {
      this.query.pageSize = val
      this.loadData()
    },
    handleCurrentChange(val) {
      this.query.pageNum = val
      this.loadData()
    }
  }
}
</script>

<style scoped>
.sku-list {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.main-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-button {
  color: #909399;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.3s;
}

.back-button:hover {
  color: #409eff;
  background: #ecf5ff;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.spu-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  padding: 6px 12px;
}

.add-button {
  border-radius: 6px;
  padding: 10px 20px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
  transition: all 0.3s;
}

.add-button:hover {
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.35);
  transform: translateY(-1px);
}

.sku-table {
  border-radius: 8px;
  overflow: hidden;
}

.sku-table :deep(.el-table__header) th {
  background: linear-gradient(180deg, #f8f9fa 0%, #f0f2f5 100%);
  color: #303133;
  font-weight: 600;
  font-size: 14px;
  padding: 14px 0;
}

.sku-table :deep(.el-table__body) tr {
  transition: background 0.25s;
}

.sku-table :deep(.el-table__body) tr:hover {
  background: #f0f7ff !important;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-name {
  color: #303133;
  font-weight: 500;
}

    .empty-cell {
      color: #c0c4cc;
      font-style: italic;
    }

    .stock-tag {
    border-radius: 20px;
    padding: 4px 12px;
    font-weight: 600;
    font-size: 14px;
    min-width: 60px;
    display: inline-block;
    text-align: center;
  }

.action-btn-edit {
    color: #409eff;
    padding: 6px 10px;
    border-radius: 4px;
    transition: all 0.3s;
  }

  .action-btn-edit:hover {
    background: #ecf5ff;
  }

  .action-btn-stock {
    color: #e6a23c;
    padding: 6px 10px;
    border-radius: 4px;
    transition: all 0.3s;
  }

  .action-btn-stock:hover {
    background: #fdf6ec;
  }

  .action-btn-record {
    color: #909399;
    padding: 6px 10px;
    border-radius: 4px;
    transition: all 0.3s;
  }

  .action-btn-record:hover {
    background: #f4f4f5;
  }

.action-btn-delete {
  color: #f56c6c;
  padding: 6px 10px;
  border-radius: 4px;
  transition: all 0.3s;
}

.action-btn-delete:hover {
  background: #fef0f0;
}

.pagination-wrapper {
  padding: 20px 0;
  display: flex;
  justify-content: flex-end;
}

.pagination {
  display: flex;
  justify-content: flex-end;
}

.pagination :deep(.el-pagination__total) {
  color: #606266;
}

.pagination :deep(.el-pagination__sizes) {
  color: #606266;
}

.pagination :deep(.el-pager li) {
  border-radius: 4px;
  margin: 0 4px;
}

.pagination :deep(.el-pager li.is-active) {
  background: #409eff;
  color: white;
}

.pagination :deep(.el-pager li:hover:not(.is-active)) {
  background: #ecf5ff;
  color: #409eff;
}
</style>
